/**
 * SelectionOverlay.tsx
 * Fullscreen overlay for drag-to-select capture area
 * 
 * Features:
 * - Semi-transparent dark overlay
 * - Blue selection rectangle (Windows Snipping Tool style)
 * - Crosshair cursor
 * - Escape key to cancel
 * - Captures canvas region on mouse release
 */

import React, { useEffect, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';
import { useCaptureAreaStore } from '../../hooks/useCaptureArea';
import { useSelectionBox, SelectionBox } from '../../hooks/useSelectionBox';

export const SelectionOverlay: React.FC = () => {
  const { isCaptureMode, cancelCapture, setCapturedImage } = useCaptureAreaStore();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Capture the selected region from the entire viewport (canvas + HTML overlays)
  const captureRegion = useCallback(async (box: SelectionBox) => {
    console.log('[Capture] Starting viewport capture with box:', box);
    
    try {
      // Find the workspace container (contains canvas + overlays)
      const workspaceContainer = document.querySelector('[data-workspace-canvas="true"]') as HTMLElement;
      
      if (!workspaceContainer) {
        console.error('[Capture] Workspace container not found');
        cancelCapture();
        return;
      }

      console.log('[Capture] Workspace container found:', {
        width: workspaceContainer.clientWidth,
        height: workspaceContainer.clientHeight,
      });

      // Get positions
      const overlayRect = overlayRef.current?.getBoundingClientRect();
      const containerRect = workspaceContainer.getBoundingClientRect();

      if (!overlayRect) {
        console.error('[Capture] No overlay rect');
        cancelCapture();
        return;
      }

      console.log('[Capture] Rects:', {
        overlay: overlayRect,
        container: containerRect,
        selectionBox: box,
      });

      // Calculate the selection box position relative to the container
      const relativeX = box.x - (containerRect.left - overlayRect.left);
      const relativeY = box.y - (containerRect.top - overlayRect.top);

      console.log('[Capture] Relative position:', { relativeX, relativeY });

      // Capture the entire workspace container with html2canvas
      console.log('[Capture] Starting html2canvas capture...');
      const fullCanvas = await html2canvas(workspaceContainer, {
        backgroundColor: '#f0f0f0', // Match canvas background
        scale: 2, // Higher quality (2x pixel density)
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: containerRect.width,
        height: containerRect.height,
      });

      console.log('[Capture] Full viewport captured:', {
        width: fullCanvas.width,
        height: fullCanvas.height,
      });

      // Calculate crop coordinates (accounting for scale)
      const scale = 2; // Must match html2canvas scale option
      const cropX = Math.max(0, relativeX * scale);
      const cropY = Math.max(0, relativeY * scale);
      const cropWidth = Math.min(fullCanvas.width - cropX, box.width * scale);
      const cropHeight = Math.min(fullCanvas.height - cropY, box.height * scale);

      console.log('[Capture] Crop region:', { cropX, cropY, cropWidth, cropHeight });

      // Create offscreen canvas for cropping
      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = cropWidth;
      offscreenCanvas.height = cropHeight;
      const ctx = offscreenCanvas.getContext('2d');

      if (!ctx) {
        console.error('[Capture] Failed to get 2D context');
        cancelCapture();
        return;
      }

      // Draw the cropped region
      ctx.drawImage(
        fullCanvas,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );

      // Get the data URL
      const dataUrl = offscreenCanvas.toDataURL('image/png');
      console.log('[Capture] Cropped data URL length:', dataUrl.length);
      console.log('[Capture] Data URL preview:', dataUrl.substring(0, 100));
      
      setCapturedImage(dataUrl);
      console.log('[Capture] Image set successfully');
    } catch (err) {
      console.error('[Capture] Capture failed:', err);
      cancelCapture();
    }
  }, [cancelCapture, setCapturedImage]);

  const { selectionBox, handlers } = useSelectionBox({
    onSelectionComplete: captureRegion,
    minSize: 20,
  });

  // Handle Escape key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelCapture();
      }
    };

    if (isCaptureMode) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCaptureMode, cancelCapture]);

  if (!isCaptureMode) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] cursor-crosshair"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      {...handlers}
    >
      {/* Instructions */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/80 text-white px-4 py-2 rounded text-sm pointer-events-none">
        Click and drag to select an area. Press <kbd className="px-1 bg-gray-700 rounded">Esc</kbd> to cancel.
      </div>

      {/* Selection box */}
      {selectionBox && selectionBox.width > 0 && selectionBox.height > 0 && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: selectionBox.x,
            top: selectionBox.y,
            width: selectionBox.width,
            height: selectionBox.height,
            backgroundColor: 'rgba(59, 130, 246, 0.3)', // blue-500 with opacity
            border: '2px solid rgb(59, 130, 246)', // blue-500
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)', // Darken outside selection
          }}
        />
      )}
    </div>
  );
};
